/**
 * LoadErrorScreen — Error de carga (pantalla completa o embebido).
 *
 * Por defecto: título «No pudimos cargar», un solo botón «Recargar página»,
 * fondo blanco a pantalla completa (`variant="app"`).
 */

import React from "react";
import { RefreshCw } from "lucide-react";
import {
  APP_LOAD_ERROR_BUTTON,
  APP_LOAD_ERROR_MESSAGE,
  APP_LOAD_ERROR_TITLE,
} from "@/lib/pageLoadError";
import { SAFE_AREA } from "@/lib/nativeShell";

export interface LoadErrorScreenProps {
  title?: string;
  message?: string;
  buttonLabel?: string;
  onRetry?: () => void;
  darkBg?: boolean;
  visual?: "error" | "empty";
  /**
   * `app` — overlay fijo a toda la ventana (rutas autenticadas / caja / inventario).
   * `full` — min-h-screen (tienda pública sin layout).
   * `embed` — bloque dentro del contenido.
   * `modal` — tarjeta centrada (legado; preferir `app`).
   */
  variant?: "app" | "full" | "embed" | "modal";
  secondaryLabel?: string;
  onSecondary?: () => void;
  footNote?: string | null;
  hideActions?: boolean;
}

const LoadErrorScreen: React.FC<LoadErrorScreenProps> = ({
  title = APP_LOAD_ERROR_TITLE,
  message = APP_LOAD_ERROR_MESSAGE,
  buttonLabel = APP_LOAD_ERROR_BUTTON,
  onRetry,
  darkBg = false,
  visual = "error",
  variant = "app",
  secondaryLabel,
  onSecondary,
  footNote = null,
  hideActions = false,
}) => {
  const handleReload = () => {
    if (onRetry) onRetry();
    else window.location.reload();
  };

  const isEmpty = visual === "empty";
  const iconWrap = isEmpty
    ? "bg-sky-100 dark:bg-sky-900/25 text-3xl"
    : "bg-amber-100 dark:bg-amber-900/30 text-3xl";

  const messageLines = message.split("\n").filter(Boolean);

  const body = (
    <div className="text-center max-w-sm mx-auto px-4">
      <div
        className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${iconWrap}`}
        aria-hidden
      >
        {isEmpty ? "📦" : "⚠️"}
      </div>
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
        {title}
      </h2>
      <div className="text-sm text-gray-600 dark:text-slate-400 mt-2 leading-relaxed space-y-1">
        {messageLines.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>
      {!hideActions ? (
        <div className="mt-6 flex flex-col gap-3 justify-center items-center">
          {onSecondary && secondaryLabel ? (
            <button
              type="button"
              onClick={onSecondary}
              className="inline-flex justify-center items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-primary-600 text-primary-700 dark:text-primary-300 font-bold text-sm hover:bg-primary-50 dark:hover:bg-slate-800 transition-all active:scale-[0.98] w-full max-w-xs"
            >
              <RefreshCw size={16} className="shrink-0" />
              {secondaryLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleReload}
            className="inline-flex justify-center items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-bold text-sm rounded-xl transition-all active:scale-95 shadow-md shadow-primary-500/20 w-full max-w-xs"
          >
            <RefreshCw size={16} className="shrink-0" />
            {buttonLabel}
          </button>
          {footNote ? (
            <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1 max-w-xs">
              {footNote}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  if (variant === "app") {
    return (
      <div
        className={`${SAFE_AREA.loadErrorApp} fixed inset-0 z-[500] flex items-center justify-center bg-white dark:bg-slate-900`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="load-error-title"
      >
        <div id="load-error-title" className="sr-only">
          {title}
        </div>
        {body}
      </div>
    );
  }

  if (variant === "modal") {
    return (
      <div
        className={`${SAFE_AREA.loadErrorApp} fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-white dark:bg-slate-900`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="load-error-title"
      >
        <div id="load-error-title" className="sr-only">
          {title}
        </div>
        {body}
      </div>
    );
  }

  if (variant === "embed") {
    return (
      <div
        className={`w-full flex justify-center px-4 py-10 ${
          darkBg ? "bg-gray-50/80 dark:bg-slate-900/40" : ""
        }`}
      >
        {body}
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 ${
        darkBg ? "bg-gray-50 dark:bg-slate-900" : "bg-white dark:bg-slate-900"
      }`}
    >
      {body}
    </div>
  );
};

export default LoadErrorScreen;
